<template>
  <div
    class="chip-multi-select"
    :class="{
      'chip-multi-select--outlined': outlined,
      'chip-multi-select--error': !!errorMessage,
      'chip-multi-select--open': isMenuOpen,
      'chip-multi-select--active': isOutlinedActive,
      'chip-multi-select--add-task': isAddTaskVariant,
      'chip-multi-select--single': single,
      'chip-multi-select--labeled': outlined && !!label,
    }"
  >
    <p
      v-if="label && !outlined && !isAddTaskVariant"
      class="chip-multi-select__label"
    >
      {{ label }}
    </p>

    <div
      v-if="isAddTaskVariant"
      ref="addTaskMenuRoot"
      class="chip-multi-select__add-task-root"
    >
      <v-menu
        ref="addTaskMenu"
        v-model="isMenuOpen"
        :close-on-content-click="false"
        :disabled="disabled || loading"
        offset-y
        fixed
        :attach="false"
        :z-index="zIndex"
        content-class="chip-multi-select__add-task-menu"
        :data-testid="`${optionTestIdPrefix}-menu`"
      >
        <template v-slot:activator="{ on, attrs }">
          <div
            ref="addTaskActivator"
            class="chip-multi-select__add-task-activator"
            :class="{ 'chip-multi-select__add-task-activator--empty': !selectedIds.length }"
            @click="onAddTaskActivatorClick($event, on)"
          >
            <button
              type="button"
              class="chip-multi-select__add-task-menu-button"
              v-bind="attrs"
              :disabled="disabled || loading"
              :aria-label="addTaskActivatorAriaLabel"
              aria-haspopup="listbox"
              :aria-expanded="isMenuOpen ? 'true' : 'false'"
              :data-testid="dataTestid"
              @click.stop="onAddTaskActivatorClick($event, on)"
              @keydown="onAddTaskActivatorKeydown($event, on)"
            />
            <div class="chip-multi-select__add-task-activator-content">
              <span
                v-if="single && selectedIds.length"
                class="chip-multi-select__single-value"
                :data-testid="`${optionTestIdPrefix}-value`"
              >
                {{ getLabel(selectedIds[0]) }}
              </span>
              <template v-else-if="!single && selectedIds.length">
                <span
                  v-for="itemId in selectedIds"
                  :key="normalizeId(itemId)"
                  class="chip-multi-select__chip"
                  :data-testid="`${optionTestIdPrefix}-chip-${itemId}`"
                  @mousedown.stop
                  @click.stop
                >
                  <span class="chip-multi-select__chip-label">
                    {{ getLabel(itemId) }}
                  </span>
                  <button
                    type="button"
                    class="chip-multi-select__chip-remove"
                    :aria-label="`Remove ${getLabel(itemId)}`"
                    :data-testid="`${optionTestIdPrefix}-chip-remove-${itemId}`"
                    :disabled="disabled || loading"
                    @click.stop="removeItem(itemId)"
                  >
                    <img
                      src="/img/icons/fbc-icons/Close-Inventory-Icon.svg"
                      alt=""
                      class="chip-multi-select__chip-remove-icon"
                    />
                  </button>
                </span>
              </template>
              <span
                v-else
                class="chip-multi-select__add-task-placeholder"
              >
                {{ placeholder }}
              </span>
            </div>
            <button
              v-if="showClearControl"
              type="button"
              class="chip-multi-select__clear-btn chip-multi-select__add-task-clear-field"
              :aria-label="`Clear ${addTaskActivatorAriaLabel}`"
              :data-testid="`${optionTestIdPrefix}-clear`"
              :disabled="disabled || loading"
              @click.stop="clearSingleSelection"
            >
              <v-icon size="16">mdi-close</v-icon>
            </button>
            <v-progress-circular
              v-if="loading"
              indeterminate
              size="16"
              width="2"
              class="chip-multi-select__add-task-chevron"
            />
            <v-icon
              v-else
              size="16"
              class="chip-multi-select__add-task-chevron"
              @click.stop="onAddTaskActivatorClick($event, on)"
            >
              mdi-chevron-down
            </v-icon>
          </div>
        </template>

        <div class="chip-multi-select__add-task-panel">
          <div
            v-if="searchable"
            class="chip-multi-select__add-task-search"
          >
            <v-text-field
              ref="addTaskMenuSearch"
              v-model="menuSearchQuery"
              dense
              outlined
              hide-details
              :placeholder="searchPlaceholder"
              append-icon="mdi-magnify"
              :data-testid="`${optionTestIdPrefix}-menu-search`"
              @click.stop
              @mousedown.stop
              @keydown.stop
            />
          </div>

          <div
            class="chip-multi-select__add-task-list"
            role="listbox"
            :aria-multiselectable="!single"
          >
            <p
              v-if="!menuItems.length"
              class="chip-multi-select__add-task-empty"
            >
              {{ emptyMessage }}
            </p>

            <template v-else>
              <button
                v-if="!single"
                type="button"
                class="chip-multi-select__add-task-row chip-multi-select__add-task-row--all"
                :data-testid="`${optionTestIdPrefix}-option-${selectAllId}`"
                @click="toggleAllMenuItems"
              >
                <v-checkbox
                  :input-value="isAllMenuItemsSelected"
                  :indeterminate="isAllMenuItemsIndeterminate"
                  color="primary"
                  hide-details
                  dense
                  class="chip-multi-select__add-task-checkbox"
                  readonly
                  @click.stop="toggleAllMenuItems"
                />
                <span class="chip-multi-select__add-task-row-label chip-multi-select__add-task-row-label--all">
                  All
                </span>
              </button>

              <button
                v-for="item in menuItems"
                :key="normalizeId(getItemId(item))"
                type="button"
                role="option"
                class="chip-multi-select__add-task-row"
                :class="{ 'chip-multi-select__add-task-row--selected': isItemSelected(getItemId(item)) }"
                :data-testid="`${optionTestIdPrefix}-option-${getItemId(item)}`"
                @click="toggleItem(getItemId(item))"
              >
                <v-checkbox
                  v-if="!single"
                  :input-value="isItemSelected(getItemId(item))"
                  color="primary"
                  hide-details
                  dense
                  class="chip-multi-select__add-task-checkbox"
                  readonly
                  @click.stop="toggleItem(getItemId(item))"
                />
                <span class="chip-multi-select__add-task-row-label">
                  {{ getItemName(item) }}
                </span>
              </button>
            </template>
          </div>

          <div
            v-if="!single"
            class="chip-multi-select__add-task-footer"
          >
            <v-btn
              class="secondaryBtn chip-multi-select__add-task-clear-btn"
              :data-testid="`${optionTestIdPrefix}-menu-clear`"
              @click="clearAllItems"
            >
              Clear
            </v-btn>
            <v-btn
              class="primary chip-multi-select__add-task-add-btn"
              :data-testid="`${optionTestIdPrefix}-menu-add`"
              @click="closeAddTaskMenu"
            >
              Add
            </v-btn>
          </div>
        </div>
      </v-menu>
    </div>

    <div
      v-else
      class="chip-multi-select__control"
      :class="{ 'chip-multi-select__control--error': !!errorMessage }"
    >
      <label
        v-if="outlined && label"
        class="chip-multi-select__float-label"
        :class="{ 'chip-multi-select__float-label--floated': isLabelFloated }"
      >
        {{ label }}
      </label>
      <v-menu
        v-model="isMenuOpen"
        offset-y
        :close-on-content-click="false"
        :disabled="disabled || loading"
        :z-index="zIndex"
        content-class="chip-multi-select__menu-content"
        :nudge-width="0"
      >
        <template v-slot:activator="{ on, attrs }">
          <div
            class="chip-multi-select__trigger"
            :class="{
              'chip-multi-select__trigger--open': isMenuOpen,
              'chip-multi-select__trigger--disabled': disabled || loading,
            }"
          >
            <button
              type="button"
              class="chip-multi-select__menu-button"
              v-bind="attrs"
              v-on="loading ? {} : on"
              :disabled="disabled || loading"
              :aria-label="outlined && label ? label : undefined"
              :data-testid="dataTestid"
            ></button>
            <div class="chip-multi-select__chips-wrap">
              <span
                v-if="single && selectedIds.length"
                class="chip-multi-select__single-value"
                :data-testid="`${optionTestIdPrefix}-value`"
              >
                {{ getLabel(selectedIds[0]) }}
              </span>
              <template v-else-if="!single">
                <span
                  v-for="itemId in selectedIds"
                  :key="normalizeId(itemId)"
                  class="chip-multi-select__chip"
                  :data-testid="`${optionTestIdPrefix}-chip-${itemId}`"
                >
                  <span class="chip-multi-select__chip-label">
                    {{ getLabel(itemId) }}
                  </span>
                  <button
                    type="button"
                    class="chip-multi-select__chip-remove"
                    :aria-label="`Remove ${getLabel(itemId)}`"
                    :data-testid="`${optionTestIdPrefix}-chip-remove-${itemId}`"
                    :disabled="disabled || loading"
                    @click.stop="removeItem(itemId)"
                  >
                    <img
                      src="/img/icons/fbc-icons/Close-Inventory-Icon.svg"
                      alt=""
                      class="chip-multi-select__chip-remove-icon"
                    />
                  </button>
                </span>
              </template>
              <span
                v-if="showPlaceholder"
                class="chip-multi-select__placeholder"
              >
                {{ placeholder }}
              </span>
            </div>
            <button
              v-if="showClearControl"
              type="button"
              class="chip-multi-select__clear-btn"
              :aria-label="`Clear ${outlined && label ? label : placeholder || 'selection'}`"
              :data-testid="`${optionTestIdPrefix}-clear`"
              :disabled="disabled || loading"
              @click.stop="clearSingleSelection"
            >
              <v-icon size="16">mdi-close</v-icon>
            </button>
            <v-progress-circular
              v-if="loading"
              indeterminate
              size="16"
              width="2"
              class="chip-multi-select__chevron"
            />
            <v-icon
              v-else
              size="16"
              class="chip-multi-select__chevron"
            >
              mdi-chevron-down
            </v-icon>
          </div>
        </template>

        <div class="chip-multi-select__menu-panel">
          <div
            v-if="searchable"
            class="chip-multi-select__menu-search"
          >
            <v-text-field
              ref="defaultMenuSearch"
              v-model="menuSearchQuery"
              dense
              outlined
              hide-details
              :placeholder="searchPlaceholder"
              append-icon="mdi-magnify"
              :data-testid="`${optionTestIdPrefix}-menu-search`"
              @click.stop
              @mousedown.stop
              @keydown.stop
            />
          </div>
          <v-list
            class="chip-multi-select__menu"
            :data-testid="`${optionTestIdPrefix}-menu`"
          >
            <v-list-item
              v-if="!single && menuItems.length"
              class="chip-multi-select__menu-item px-2"
              :data-testid="`${optionTestIdPrefix}-option-${selectAllId}`"
              @click="toggleItem(selectAllId)"
            >
              <v-checkbox
                :input-value="isAllSelected"
                :ripple="false"
                hide-details
                color="caccounting"
                class="pa-0 ma-0"
                @click.stop="toggleItem(selectAllId)"
              >
                <template v-slot:label>{{ selectAllLabel }}</template>
              </v-checkbox>
            </v-list-item>
            <v-list-item
              v-if="!menuItems.length"
              class="chip-multi-select__menu-item chip-multi-select__menu-item--empty px-2"
              :data-testid="`${optionTestIdPrefix}-menu-empty`"
            >
              <span class="chip-multi-select__menu-empty">No results found</span>
            </v-list-item>
            <v-list-item
              v-for="item in menuItems"
              :key="normalizeId(getItemId(item))"
              class="chip-multi-select__menu-item px-2"
              :class="{ 'chip-multi-select__menu-item--selected': single && isItemSelected(getItemId(item)) }"
              :data-testid="`${optionTestIdPrefix}-option-${getItemId(item)}`"
              @click="toggleItem(getItemId(item))"
            >
              <span
                v-if="single"
                class="chip-multi-select__menu-item-label"
              >
                {{ getItemName(item) }}
              </span>
              <v-checkbox
                v-else
                :input-value="isItemSelected(getItemId(item))"
                :ripple="false"
                hide-details
                color="caccounting"
                class="pa-0 ma-0"
                @click.stop="toggleItem(getItemId(item))"
              >
                <template v-slot:label>{{ getItemName(item) }}</template>
              </v-checkbox>
            </v-list-item>
          </v-list>
        </div>
      </v-menu>
    </div>

    <div
      v-if="errorMessage"
      class="v-messages error--text chip-multi-select__error"
      role="alert"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import './ChipMultiSelect.scss';
import { DIALOG_MENU_Z_INDEX, positionMenuNearActivator } from '@/utils/sync-attached-menu';
import { LOCATION_SELECT_ALL } from '../../utils/constants';
import {
  getAllItemValues,
  getItemLabel,
  getItemValue,
  isAllValuesSelected,
  isValueSelected,
  normalizeId,
  removeValue,
  toggleSelectAllValues,
  toggleValue,
} from './ChipMultiSelectUtils';
import { displayLocationLabel } from '../../utils/locationName';

/** Chip multi-select with a Select All checkbox that stays in sync with the selection. */
export default {
  name: 'ChipMultiSelect',

  inheritAttrs: false,

  props: {
    value: {
      type: [Array, String, Number],
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    itemText: {
      type: String,
      default: 'name',
    },
    itemValue: {
      type: String,
      default: 'id',
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Select Entity/Location',
    },
    error: {
      type: [String, Array],
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Single-select mode. Default is multi-select chips.
     * When true, v-model is one id and the closed field shows that label as text, not a badge.
     * No Select All, chips, or Clear/Add footer in this mode.
     */
    single: {
      type: Boolean,
      default: false,
    },
    /**
     * Single-select only: show a clear (X) control on the closed field when a value is selected.
     */
    clearable: {
      type: Boolean,
      default: true,
    },
    /**
     * Show a search field inside the dropdown menu. Defaults on so existing
     * multi-select callers gain filter-as-you-type without prop changes.
     */
    searchable: {
      type: Boolean,
      default: true,
    },
    searchPlaceholder: {
      type: String,
      default: 'Search',
    },
    emptyMessage: {
      type: String,
      default: 'No locations found',
    },
    dataTestid: {
      type: String,
      default: 'chip-multi-select',
    },
    dataTestidPrefix: {
      type: String,
      default: '',
    },
    zIndex: {
      type: Number,
      default: DIALOG_MENU_Z_INDEX,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'add-task'].includes(value),
    },
  },

  data() {
    return {
      isMenuOpen: false,
      menuSearchQuery: '',
      addTaskScrollCloseBinding: null,
      menuPinTimer: null,
      ignoreScrollClose: false,
      scrollIgnoreTimer: null,
    };
  },

  computed: {
    isAddTaskVariant() {
      return this.variant === 'add-task';
    },

    selectAllId() {
      return LOCATION_SELECT_ALL.ID;
    },

    selectAllLabel() {
      return LOCATION_SELECT_ALL.LABEL;
    },

    selectedIds() {
      if (this.single) {
        const v = this.value;
        if (v === undefined || v === null || v === '') return [];
        if (Array.isArray(v)) return v.length ? [v[0]] : [];
        return [v];
      }
      return this.value || [];
    },

    allItemIds() {
      return getAllItemValues(this.items, this.itemValue);
    },

    isAllSelected() {
      return isAllValuesSelected(this.selectedIds, this.allItemIds);
    },

    menuItems() {
      const term = (this.menuSearchQuery || '').trim().toLowerCase();
      return (this.items || []).filter((item) => {
        const label = this.getItemName(item).toLowerCase();
        return !term || label.includes(term);
      });
    },

    isAllMenuItemsSelected() {
      const visible = this.menuItems;
      if (!visible.length) return false;
      return visible.every((item) => this.isItemSelected(this.getItemId(item)));
    },

    isAllMenuItemsIndeterminate() {
      const visible = this.menuItems;
      if (!visible.length) return false;
      const selectedCount = visible.filter((item) => this.isItemSelected(this.getItemId(item))).length;
      return selectedCount > 0 && selectedCount < visible.length;
    },

    errorMessage() {
      if (Array.isArray(this.error)) {
        return this.error.filter(Boolean).join(' ');
      }
      return this.error || '';
    },

    optionTestIdPrefix() {
      return this.dataTestidPrefix || this.dataTestid;
    },

    isLabelFloated() {
      return this.outlined && (this.isMenuOpen || this.selectedIds.length > 0);
    },

    isOutlinedActive() {
      return this.isLabelFloated;
    },

    showPlaceholder() {
      if (this.selectedIds.length || !this.placeholder) {
        return false;
      }
      if (this.outlined) {
        return this.isMenuOpen;
      }
      return true;
    },

    addTaskActivatorAriaLabel() {
      return this.label || this.placeholder || 'Select Entity/Location';
    },

    showClearControl() {
      return (
        this.single
        && this.clearable
        && this.selectedIds.length > 0
        && !this.disabled
        && !this.loading
      );
    },
  },

  watch: {
    value() {
      this.refreshAddTaskMenuPosition();
    },
    loading(isLoading) {
      if (isLoading && this.isMenuOpen) {
        this.isMenuOpen = false;
      }
    },
    isMenuOpen(open) {
      if (open) {
        this.menuSearchQuery = '';
        if (this.isAddTaskVariant) {
          this.suppressScrollClose();
          this.$nextTick(() => {
            requestAnimationFrame(() => {
              this.pinAddTaskMenu();
              this.focusMenuSearch();
              if (this.menuPinTimer) window.clearTimeout(this.menuPinTimer);
              this.menuPinTimer = window.setTimeout(() => {
                this.menuPinTimer = null;
                if (!this.isMenuOpen) return;
                this.pinAddTaskMenu();
              }, 50);
            });
          });
        } else if (this.searchable) {
          this.$nextTick(() => {
            this.focusMenuSearch();
          });
        }
      } else {
        this.menuSearchQuery = '';
      }
    },
    menuSearchQuery() {
      this.refreshAddTaskMenuPosition();
    },
  },

  mounted() {
    this.$nextTick(() => {
      if (this.isAddTaskVariant) {
        this.bindAddTaskMenuScrollClose();
      }
    });
  },

  updated() {
    this.$nextTick(() => {
      if (this.isAddTaskVariant) {
        this.bindAddTaskMenuScrollClose();
      }
    });
  },

  beforeDestroy() {
    this.isMenuOpen = false;
    this.unbindAddTaskMenuScrollClose();
    if (this.menuPinTimer) {
      window.clearTimeout(this.menuPinTimer);
      this.menuPinTimer = null;
    }
    if (this.scrollIgnoreTimer) {
      window.clearTimeout(this.scrollIgnoreTimer);
      this.scrollIgnoreTimer = null;
    }
  },

  methods: {
    normalizeId,

    getItemId(item) {
      return getItemValue(item, this.itemValue);
    },

    getItemName(item) {
      return getItemLabel(item, this.itemText);
    },

    getLabel(id) {
      const match = (this.items || []).find(
        (item) => normalizeId(this.getItemId(item)) === normalizeId(id),
      );
      return displayLocationLabel(match ? this.getItemName(match) : id);
    },

    isItemSelected(id) {
      if (id === this.selectAllId) {
        return this.isAllSelected;
      }
      return isValueSelected(this.selectedIds, id);
    },

    emitSelection(next) {
      if (this.single) {
        let id = null;
        if (Array.isArray(next)) {
          id = next.length ? next[0] : null;
        } else if (next !== undefined && next !== null && next !== '') {
          id = next;
        }
        this.$emit('input', id);
        this.$emit('change', id);
        return;
      }
      this.$emit('input', next);
      this.$emit('change', next);
    },

    focusMenuSearch() {
      if (!this.searchable) return;
      const field = this.isAddTaskVariant
        ? this.$refs.addTaskMenuSearch
        : this.$refs.defaultMenuSearch;
      const input = field?.$el?.querySelector('input');
      input?.focus();
    },

    toggleItem(id) {
      if (this.disabled || this.loading) return;
      if (this.single) {
        if (id === this.selectAllId) return;
        this.emitSelection(id);
        this.isMenuOpen = false;
        this.menuSearchQuery = '';
        if (this.isAddTaskVariant) {
          this.suppressScrollClose(400);
        }
        return;
      }
      const next = id === this.selectAllId
        ? toggleSelectAllValues(this.selectedIds, this.allItemIds)
        : toggleValue(this.selectedIds, id);
      this.emitSelection(next);
      if (this.isAddTaskVariant) {
        this.suppressScrollClose(400);
      }
    },

    toggleAllMenuItems() {
      if (this.single || this.disabled || this.loading) return;
      const visibleIds = this.menuItems.map((item) => this.getItemId(item));
      if (!visibleIds.length) return;

      const allSelected = visibleIds.every((id) => isValueSelected(this.selectedIds, id));
      if (allSelected) {
        const visibleSet = new Set(visibleIds.map((id) => normalizeId(id)));
        this.emitSelection(
          this.selectedIds.filter((id) => !visibleSet.has(normalizeId(id))),
        );
      } else {
        const next = [...this.selectedIds];
        visibleIds.forEach((id) => {
          if (!isValueSelected(next, id)) {
            next.push(id);
          }
        });
        this.emitSelection(next);
      }
      this.suppressScrollClose(400);
    },

    clearAllItems() {
      this.emitSelection(this.single ? null : []);
      this.menuSearchQuery = '';
    },

    clearSingleSelection() {
      if (!this.single || this.disabled || this.loading) return;
      this.emitSelection(null);
      this.isMenuOpen = false;
      this.menuSearchQuery = '';
    },

    closeAddTaskMenu() {
      this.isMenuOpen = false;
      this.menuSearchQuery = '';
    },

    removeItem(id) {
      if (this.disabled || this.loading) return;
      if (this.single) {
        this.emitSelection(null);
        return;
      }
      this.emitSelection(removeValue(this.selectedIds, id));
    },

    onAddTaskActivatorClick(event, on) {
      if (this.disabled || this.loading) return;
      if (event?.target?.closest?.('.chip-multi-select__chip-remove')) return;
      if (event?.target?.closest?.('.chip-multi-select__clear-btn')) return;
      if (typeof on?.click === 'function') on.click(event);
    },

    onAddTaskActivatorKeydown(event, on) {
      if (this.disabled || this.loading) return;
      const isActivateKey = event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar';
      if (!isActivateKey) return;
      event.preventDefault();
      this.onAddTaskActivatorClick(event, on);
    },

    refreshAddTaskMenuPosition() {
      if (!this.isAddTaskVariant || !this.isMenuOpen) return;
      this.$nextTick(() => {
        this.pinAddTaskMenu();
        requestAnimationFrame(() => {
          if (!this.isMenuOpen) return;
          this.pinAddTaskMenu();
        });
      });
    },

    pinAddTaskMenu() {
      if (!this.isAddTaskVariant || !this.isMenuOpen) return;
      const activatorEl = this.$refs.addTaskActivator;
      if (!activatorEl) return;
      positionMenuNearActivator({
        activatorEl,
        menuClass: 'chip-multi-select__add-task-menu',
        fieldRef: this.$refs.addTaskMenu,
        maxMenuHeight: 320,
        prefer: 'above',
      });
    },

    suppressScrollClose(ms = 250) {
      this.ignoreScrollClose = true;
      if (this.scrollIgnoreTimer) window.clearTimeout(this.scrollIgnoreTimer);
      this.scrollIgnoreTimer = window.setTimeout(() => {
        this.ignoreScrollClose = false;
        this.scrollIgnoreTimer = null;
      }, ms);
    },

    findScrollParent(node) {
      let el = node?.parentElement;
      while (el) {
        const style = window.getComputedStyle(el);
        const overflow = `${style.overflow}${style.overflowY}${style.overflowX}`;
        if (/(auto|scroll|overlay)/.test(overflow)) return el;
        el = el.parentElement;
      }
      return null;
    },

    bindAddTaskMenuScrollClose() {
      if (!this.isAddTaskVariant || this.addTaskScrollCloseBinding) return;
      const parent = this.findScrollParent(this.$el);
      if (!parent) return;
      const handler = () => {
        if (this.ignoreScrollClose) return;
        if (this.isMenuOpen) this.closeAddTaskMenu();
      };
      parent.addEventListener('scroll', handler, { passive: true });
      this.addTaskScrollCloseBinding = { parent, handler };
    },

    unbindAddTaskMenuScrollClose() {
      if (!this.addTaskScrollCloseBinding) return;
      const { parent, handler } = this.addTaskScrollCloseBinding;
      parent.removeEventListener('scroll', handler);
      this.addTaskScrollCloseBinding = null;
    },
  },
};
</script>
